import { SeaEventTypeEnum } from '../../enums/seaEventEnum';
import { SeaEventTypeInputEnum } from '../../enums/seaEventTypeInputEnum';
import { Port } from '../../types/ViewModel/Port';
import { PortCall } from '../../types/ViewModel/PortCall';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import { TimeZone } from '../../types/ViewModel/TimeZone';
import { EventDate } from '../../types/ViewModel/EventDate';
import { isDateAfter, isDateBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import { formatDateProperties } from 'odfjell-date-time-utils/dateTimeFormatter';
import {
    dateToTimeMs,
    floorTimeMsToMinute,
    getExactTimeMs,
    timeMsToLocalDate,
} from '../../service-worker/offline_handlers/aol/event-time-keys.js';

const toLocalTimeString = (timeMs: number): string => new Date(timeMs).toISOString().slice(0, 19);

/**
 * Replaces the Date properties of an event date with the 'yyyy-MM-ddTHH:mm:ss' strings that are saved.
 * Actual UTC and local are written from the exact local time remembered on their Date (UTC getters), so a time that the browser
 * parsed into its own DST gap is saved unchanged. Dates without a valid remembered local time (replaced or edited to another
 * minute) are formatted from their fields as before.
 */
export const formatEventDateProperties = (eventDate: EventDate | null | undefined): void => {
    if (!eventDate) return;
    const utcTimeMs = getExactTimeMs(eventDate.actualDateTimeUtc);
    const localTimeMs = getExactTimeMs(eventDate.actualDateTimeLocal);
    formatDateProperties(eventDate);
    if (utcTimeMs !== null) (eventDate as unknown as Record<string, unknown>).actualDateTimeUtc = toLocalTimeString(utcTimeMs);
    if (localTimeMs !== null) (eventDate as unknown as Record<string, unknown>).actualDateTimeLocal = toLocalTimeString(localTimeMs);
};

export const getDateWithoutSecMs = (evDate: Date | null): Date | null => {
    if (evDate) {
        evDate.setSeconds(0, 0);
        return evDate;
    }
    return null;
};

// Exact UTC time (ms, the Date's UTC fields, not the browser-local Date) of a UTC Date, to the minute (see event-time-keys.js), or null.
const getMinuteTimeMs = (date: Date | null | undefined): number | null => {
    if (!date) return null;
    const timeMs = dateToTimeMs(date);
    return Number.isNaN(timeMs) ? null : floorTimeMsToMinute(timeMs);
};

export const isLocalOrderOkButUtcViolated = (
    conflict: TankersEvent | null,
    candidateLocal: Date | null,
    candidateUtc: Date | null
): boolean => {
    if (!conflict?.eventDate.actualDateTimeLocal || !conflict.eventDate.actualDateTimeUtc || !candidateLocal || !candidateUtc) return false;
    const conflictLocal = getDateWithoutSecMs(new Date(conflict.eventDate.actualDateTimeLocal.getTime()));
    const local = getDateWithoutSecMs(new Date(candidateLocal.getTime()));
    const conflictUtc = getDateWithoutSecMs(new Date(conflict.eventDate.actualDateTimeUtc.getTime()));
    const utc = getDateWithoutSecMs(new Date(candidateUtc.getTime()));
    return !isDateAfter(conflictLocal, local) && isDateAfter(conflictUtc, utc);
};

export const compareActualEventDateTime = (a: TankersEvent, b: TankersEvent, desc: boolean): number => {
    let cmp = 0;
    if (isDateAfter(a.eventDate.actualDateTimeUtc, b.eventDate.actualDateTimeUtc)) {
        cmp = 1;
    } else if (isDateBefore(a.eventDate.actualDateTimeUtc, b.eventDate.actualDateTimeUtc)) {
        cmp = -1;
    }
    return desc ? cmp * -1 : cmp;
};

export const compareActualEventDateTimeWithoutSecMs = (a: TankersEvent, b: TankersEvent, desc: boolean): number => {
    const dateA = getDateWithoutSecMs(a.eventDate.actualDateTimeUtc);
    const dateB = getDateWithoutSecMs(b.eventDate.actualDateTimeUtc);
    let cmp = 0;
    if (isDateAfter(dateA, dateB)) {
        cmp = 1;
    } else if (isDateBefore(dateA, dateB)) {
        cmp = -1;
    }
    return desc ? cmp * -1 : cmp;
};

export const createUtcTimeAdjustTimezones = (portCall: PortCall, ev: TankersEvent, localTime: Date, evArray: TankersEvent[] | null): Date => {
    // All arithmetic is done in epoch milliseconds on the local time (Date.UTC fields), so the browser zone and its DST
    // transitions never add or remove an hour. Only the final value is turned into a Date.
    const utcTimeMs = getUtcTimeMs(portCall?.port, localTime);
    const utc = timeMsToLocalDate(utcTimeMs);
    // Filter out the current event to avoid circular reference when calculating timezone adjustments
    const eventsForCalculation = ev?.eventId && evArray ? evArray.filter((e) => e.eventId !== ev.eventId) : evArray;

    let adj = getAdjustedTimeZoneHours(portCall, utc, eventsForCalculation);
    const adjIDL = getAdjustedIDLCrossingHours(portCall, ev, utc, eventsForCalculation);
    adj += adjIDL;
    return timeMsToLocalDate(utcTimeMs + Math.round(adj * MS_PER_HOUR));
};

const getAdjustedHours = (ev: TankersEvent, evTypeInputId: SeaEventTypeInputEnum): number => {
    let adjustHours = 0;
    if (ev.eventTypeInputRegList && ev.eventTypeInputRegList.length > 0) {
        // Check for 'Number of hours' dropdown input
        const evInp = ev.eventTypeInputRegList.find((evInpReg) => evInpReg.eventTypeInputId === evTypeInputId);
        if (
            evInp &&
            evInp.dropdownGroupReg &&
            evInp.dropdownGroupReg.dropdownItemRegList &&
            evInp.dropdownGroupReg.dropdownItemRegList.length === 1
        ) {
            const hoursStr = evInp.dropdownGroupReg.dropdownItemRegList[0].dropdownItemValue;
            const hours: number = +hoursStr.substring(0, hoursStr.length - 1);
            adjustHours += hours;
        }
    }
    return adjustHours;
};

const getAdjustedTimeZoneHours = (portCall: PortCall, datetime: Date, evArray: TankersEvent[] | null): number => {
    if (!evArray) return 0;
    let adjustHours = 0;

    const evListPc = evArray.filter((ev) => ev.portCallId === portCall?.portCallId && !ev.deleted);

    // Eligibility is compared on exact UTC times in ms (to the minute), never on browser-local Dates: a derived 02:30Z
    // would otherwise normalise to 03:30 in a Europe/Oslo browser and wrongly appear after a 03:00Z adjustment.
    const candidateMs = getMinuteTimeMs(datetime);
    const evList = evListPc.filter((ev) => {
        // FIX: Use actualDateTimeUtc directly instead of getAdjustedDate to avoid circular dependency
        // The timezone event's actualDateTimeUtc is already in the ORIGINAL timezone (before the timezone change it represents)
        // So we should compare directly without applying the adjustment hours backwards
        const eventMs = getMinuteTimeMs(ev.eventDate?.actualDateTimeUtc);
        const isTimeZoneEvent = ev.eventTypeId === SeaEventTypeEnum.AdjustHoursAfterTimeZoneChange;
        // Same minute or later (equality counts).
        return isTimeZoneEvent && candidateMs !== null && eventMs !== null && candidateMs >= eventMs;
    });
    evList.forEach((ev) => {
        const hours = getAdjustedHours(ev, SeaEventTypeInputEnum.AdjustHoursAfterTimeZoneChangeInput);
        adjustHours += hours;
    });
    return adjustHours;
};

const getAdjustedIDLCrossingHours = (portCall: PortCall, actualEv: TankersEvent, datetime: Date, evArray: TankersEvent[] | null): number => {
    if (!evArray) {
        // Still check the actualEv for IDL crossing adjustment even without an events array
        if (actualEv.eventTypeId === SeaEventTypeEnum.IDLCrossing) {
            return getAdjustedHours(actualEv, SeaEventTypeInputEnum.IDLCrossingInput);
        }
        return 0;
    }
    let adjustHours = 0;

    const evListPc = evArray.filter((ev) => ev.portCallId === portCall?.portCallId && !ev.deleted);
    const candidateMs = getMinuteTimeMs(datetime);
    const evList = evListPc.filter((ev) => {
        // FIX: Use actualDateTimeUtc directly instead of getAdjustedDate to avoid circular dependency
        // The IDL crossing event's actualDateTimeUtc is already in the ORIGINAL timezone (before the IDL crossing it represents)
        const eventMs = getMinuteTimeMs(ev.eventDate?.actualDateTimeUtc);
        // Strictly later (equality does not count).
        return ev.eventTypeId === SeaEventTypeEnum.IDLCrossing && candidateMs !== null && eventMs !== null && candidateMs > eventMs;
    });
    evList.forEach((ev) => {
        adjustHours += getAdjustedHours(ev, SeaEventTypeInputEnum.IDLCrossingInput);
    });
    if (actualEv.eventTypeId === SeaEventTypeEnum.IDLCrossing) {
        adjustHours += getAdjustedHours(actualEv, SeaEventTypeInputEnum.IDLCrossingInput);
    }
    return adjustHours;
};

const getUtcTimeMs = (port: Port | null | undefined, localTime: Date): number => {
    if (!port) {
        console.warn('[eventDateUtils] getUtcTime called with null port — UTC conversion will treat local time as UTC');
    }
    const offset = getImosOffset(port ?? null, localTime);
    return convertLocalTimeMsToUtcTimeMs(dateToTimeMs(localTime), offset);
};

export const getImosOffset = (port: Port | null, localTime: Date): number => {
    let offset = 0;
    if (port) {
        port.timeZoneArray.forEach((tz) => {
            if (tz.year === localTime.getFullYear()) {
                if (isDaylightSaving(tz)) {
                    if (isNorthernHemisphere(tz)) {
                        if (localTimeIsInNorthernHemisphereDst(tz, localTime)) {
                            offset = tz.utcOffsetDst;
                        } else {
                            offset = tz.utcOffset;
                        }
                    } else {
                        if (localTimeIsInSouthernHemisphereDst(tz, localTime)) {
                            // Southern dst is opposite of dst for northern Hemisphere
                            offset = tz.utcOffsetDst;
                        } else {
                            offset = tz.utcOffset;
                        }
                    }
                } else {
                    offset = tz.utcOffset;
                }
            }
        });
    }
    return offset;
};

/**
 * Port UTC offset (hours) in force at a true instant (epoch ms). Unlike getImosOffset, which takes a port local time, this does
 * not read the browser zone: the DST boundaries are turned into instants from their exact local time (see TimeZoneFactory).
 *
 * The TimeZone row is the one whose year equals the UTC year of the instant projected with that row's standard offset.
 * A southern-hemisphere row of year Y holds the DST end (early in Y) and the DST start (late in Y), so
 * `instant >= start || instant < end` covers January and December of Y. The projection uses the standard offset, so a DST
 * local time just after midnight on 1 January still selects row Y, where the instant is at or after the DST start. Both
 * ways give DST. No matching row gives 0, as getImosOffset does.
 * DST starts at a local time in standard time and ends at a local time in DST (the end is exclusive, as in UtcConverter).
 */
export const getImosOffsetAtInstant = (port: Port | null, instantMs: number): number => {
    if (!port) return 0;
    const tz = port.timeZoneArray.find((row) => new Date(instantMs + toOffsetMs(row.utcOffset)).getUTCFullYear() === row.year);
    if (!tz) return 0;
    if (!tz.dstStartLocalTime || !tz.dstEndLocalTime) return tz.utcOffset;

    const dstStartTimeMs = dateToTimeMs(tz.dstStartLocalTime);
    const dstEndTimeMs = dateToTimeMs(tz.dstEndLocalTime);
    const startInstantMs = dstStartTimeMs - toOffsetMs(tz.utcOffset);
    const endInstantMs = dstEndTimeMs - toOffsetMs(tz.utcOffsetDst);
    const isDst =
        dstStartTimeMs < dstEndTimeMs
            ? instantMs >= startInstantMs && instantMs < endInstantMs
            : instantMs >= startInstantMs || instantMs < endInstantMs;
    return isDst ? tz.utcOffsetDst : tz.utcOffset;
};

export const convertToLocalTime = (date: Date, port: Port | null): Date => {
    const imosOffset = getImosOffsetAtInstant(port, date.getTime());
    // Port local time = instant + port offset, in fixed milliseconds. Reading the machine offset or using setHours would
    // truncate negative fractional offsets (-3.5h) and be distorted by the browser's DST transitions.
    const portTimeMs = date.getTime() + toOffsetMs(imosOffset);
    return timeMsToLocalDate(portTimeMs);
};

const isDaylightSaving = (tz: TimeZone): boolean => {
    return tz.dstStartLocalTime != null;
};

const isNorthernHemisphere = (tz: TimeZone): boolean => {
    if (tz.dstStartLocalTime && tz.dstEndLocalTime) {
        return tz.dstStartLocalTime < tz.dstEndLocalTime;
    }
    return false;
};

const localTimeIsInNorthernHemisphereDst = (tz: TimeZone, localTime: Date): boolean => {
    if (tz.dstStartLocalTime && tz.dstEndLocalTime) {
        return localTime >= tz.dstStartLocalTime && localTime <= tz.dstEndLocalTime;
    }
    return false;
};

const localTimeIsInSouthernHemisphereDst = (tz: TimeZone, localTime: Date): boolean => {
    if (tz.dstStartLocalTime && tz.dstEndLocalTime) {
        return tz.dstStartLocalTime <= localTime || localTime <= tz.dstEndLocalTime;
    }
    return false;
};

const MS_PER_HOUR = 60 * 60 * 1000;

// Offsets are hours (can be fractional, e.g. +5.5 or -3.5). Rounded to whole minutes, as the previous implementation did.
const toOffsetMs = (offsetHours: number): number => Math.round(offsetHours * 60) * 60 * 1000;

// localTimeMs encodes the port-local time the user entered as Date.UTC fields. Subtracting the port UTC offset gives the
// UTC time in the same encoding. Fixed-millisecond arithmetic: it never reads the browser zone or its DST rules.
const convertLocalTimeMsToUtcTimeMs = (localTimeMs: number, imosUtcOffset: number): number => {
    if (isNaN(imosUtcOffset)) {
        return localTimeMs; // Cannot convert it if imosUtcOffset is not valid.
    }
    return localTimeMs - toOffsetMs(imosUtcOffset);
};
